/* ============================================================================
 * Project        : Deloitte Digital Compliance
 * File           : RaiseTicketRightTemplate.jsx
 * Version        : 1.0.0
 * Author         : Team Neubinary
 * Created On     : 2026-06-24
 *
 * Description    : Digital Compliance for Automation and Governance
 *
 * Change History :
 * ----------------------------------------------------------------------------
 * Date         | Version | Author           | Description
 * ----------------------------------------------------------------------------
 * 2026-06-24   | 1.0.0   | Team Neubinary   | Initial creation
 * <YYYY-MM-DD> | x.x.x   | <Name/Team>      | <Change description>
 * ============================================================================
 *
 * Copyright (c) 2026 AU79 Consulting Services Pvt. Ltd.
 * All Rights Reserved.
 *
 * This source code and its contents are confidential and proprietary to
 * AU79 Consulting Services Pvt. Ltd. Unauthorized copying, reproduction,
 * modification, distribution, or disclosure of this material, in whole or
 * in part, is strictly prohibited without prior written permission from
 * AU79 Consulting Services Pvt. Ltd.
 * ============================================================================
 */
import { Box, Grid, Typography, Tooltip } from "@mui/material";
import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import CustomFormTextFieldV2 from "../../../components/customComponentsV2/common/textField/CustomFormTextFieldV2";
import { componentStyle } from "../../../config/appTheme";
import CustomMultiSelectV2 from "../../../components/customComponentsV2/common/multiselect/CustomMultiSelectV2";
import CustomTabsV2 from "../../../components/customComponentsV2/common/tabs/CustomTabsV2";
import { useDispatch, useSelector } from "react-redux";
import CustomAutoCompleteWithAPIV2 from "../../../components/customComponentsV2/common/searchBar/CustomAutoCompleteWithAPIV2";
import typeApprovalAPI from "../../../redux/actions/typeApprovalAPI";
import CustomButtonV2 from "../../../components/customComponentsV2/common/button/CustomButtonV2";
import AddButtonV2 from "../../../components/customComponentsV2/common/button/AddButtonV2";
import DynamicFormRenderer from "../../admin-settings/DynamicFormV2/DynamicFormRenderer";
import { DynamicFormDialog } from "../../../components";
import { HandleApiActions, userManagementApis } from "../../../redux/actions";

function getComponentNameForUser(el, formType, selectedComponent, userObj) {
  if (formType === "ee") {
    return (
      el.component_description ||
      (Array.isArray(selectedComponent)
        ? selectedComponent
            .map((c) => c.description)
            .filter(Boolean)
            .join(", ")
        : selectedComponent?.description)
    );
  }
  if (formType === "swm") {
    return (
      el.component_name ||
      (Array.isArray(selectedComponent)
        ? selectedComponent
            .map((c) => c.name)
            .filter(Boolean)
            .join(", ")
        : selectedComponent?.name)
    );
  }
  if (formType === "vehicle-system") {
    return (
      el.component_name ||
      (Array.isArray(selectedComponent)
        ? selectedComponent
            .map((c) => c.description)
            .filter(Boolean)
            .join(", ")
        : selectedComponent?.description)
    );
  }
  if (formType === "vehicle-component") {
    return (
      el.component_name ||
      (Array.isArray(selectedComponent)
        ? selectedComponent
            .map(
              (c) =>
                (c.vcData || [])
                  .map((vc) => vc.component_number)
                  .join(", ") || c.component_number,
            )
            .filter(Boolean)
            .join(", ")
        : (selectedComponent?.vcData || [])
            .map((vc) => vc.component_number)
            .join(", ") || selectedComponent?.component_number)
    );
  }
  return (
    userObj.component_name ||
    userObj.component ||
    el.component_name ||
    "Component"
  );
}

function renderUserStatusTag(option) {
  if (option.is_deleted) {
    return (
      <span style={{ color: "red", marginLeft: "8px" }}>
        (deleted)
      </span>
    );
  }
  if (option.is_active === false) {
    return (
      <span style={{ color: "red", marginLeft: "8px" }}>
        (deactivated)
      </span>
    );
  }
  return null;
}

function RaiseTicketRightTemplate({
  selectedEEMain,
  selectedComponentRequestor = [],
  selectedLegalTechUserDetails = [],
  selectedComponentSupplierDetails = [],
  setSelectedComponentSupplierDetails,
  selectedComplianceRequestors,
  selectedComplianceReviewers,
  setSelectedComplianceRequestor,
  setSelectedComplianceReviewers,
  selectedComplianceSupplier,
  setSelectedComplianceSupplier,
  formType,
  selectedDynamicForm,
  setSelectedDynamicForm,
  handleLocalSaveForm,
  localSubmitDisabled,
  description,
  setDescription,
  savedLinkageCard,
  dynamicFormFilteredData,
  setDynamicFormFilteredData,
  showDynamicSelect = false,
  setShowDynamicSelect,
  selectedComponent,
}) {
  const dispatch = useDispatch();
  const selectedProjectDetails = useSelector(
    (state) => state?.projectConfigReducer?.selectedProjectDetails,
  );
  const selectedVariantDetails = useSelector(
    (state) => state.projectConfigReducer.selectedVariantDetails,
  );
  const [isDynamicFormDialogOpen, setIsDynamicFormDialogOpen] = useState(false);
  const [openEditDynamicForm, setOpenEditDynamicForm] = useState(false);
  const [users, setUsers] = useState([]);

  useEffect(() => {
    Promise.resolve(
      dispatch(
        HandleApiActions({
          ...userManagementApis.get_users_list_pa,
          show_toast: false,
          params: { page_number: 1, page_size: 10000 },
        }),
      ),
    ).then((resp) => {
      if (!resp?.error) setUsers(resp.data || []);
    });
  }, [dispatch]);

  const handelEditDynamicForm = () => {
    if (selectedDynamicForm?.template_tag === "NEW") {
      setOpenEditDynamicForm(!openEditDynamicForm);
    } else if (selectedDynamicForm?.template_tag === "OLD") {
      setIsDynamicFormDialogOpen(!isDynamicFormDialogOpen);
    }
  };

  const handleDynamicFormDialog = () => {
    setIsDynamicFormDialogOpen(false);
  };

  const handleAddDescDialogClose = () => {
    setIsDynamicFormDialogOpen(false);
  };

  const formTypeId =
    { ee: 200, swm: 100, "vehicle-system": 400, "vehicle-component": 300 }[
      formType
    ] ?? null;

  useEffect(() => {
    if (
      users.length > 0 &&
      Array.isArray(selectedComplianceReviewers) &&
      selectedComplianceReviewers.length > 0
    ) {
      const activeReviewers = selectedComplianceReviewers.filter((reviewer) => {
        const revId = reviewer.value || reviewer.user_id;
        const userObj = users.find((u) => String(u.user_id) === String(revId));
        const isDeleted =
          !userObj || userObj.is_deleted === true || userObj.deleted === true;
        const isDeactivated =
          userObj?.is_active === false ||
          userObj?.is_active === 0 ||
          userObj?.status === "inactive";
        return !isDeleted && !isDeactivated;
      });
      if (activeReviewers.length !== selectedComplianceReviewers.length) {
        setSelectedComplianceReviewers(activeReviewers);
      }
    }
  }, [users, selectedComplianceReviewers, setSelectedComplianceReviewers]);

  useEffect(() => {
    if (
      users.length > 0 &&
      Array.isArray(selectedComplianceRequestors) &&
      selectedComplianceRequestors.length > 0
    ) {
      const activeRequestors = selectedComplianceRequestors.filter((requestor) => {
        const reqId = requestor.value || requestor.user_id;
        const userObj = users.find((u) => String(u.user_id) === String(reqId));
        const isDeleted =
          !userObj || userObj.is_deleted === true || userObj.deleted === true;
        const isDeactivated =
          userObj?.is_active === false ||
          userObj?.is_active === 0 ||
          userObj?.status === "inactive";
        return !isDeleted && !isDeactivated;
      });
      if (activeRequestors.length !== selectedComplianceRequestors.length) {
        setSelectedComplianceRequestor(activeRequestors);
      }
    }
  }, [users, selectedComplianceRequestors, setSelectedComplianceRequestor]);

  useEffect(() => {
    if (
      users.length > 0 &&
      Array.isArray(selectedComplianceSupplier) &&
      selectedComplianceSupplier.length > 0
    ) {
      const activeSuppliers = selectedComplianceSupplier.filter((sup) => {
        const supId = sup.value || sup.user_id;
        const userObj = users.find((u) => String(u.user_id) === String(supId));
        const isDeleted =
          !userObj || userObj.is_deleted === true || userObj.deleted === true;
        const isDeactivated =
          userObj?.is_active === false ||
          userObj?.is_active === 0 ||
          userObj?.status === "inactive";
        return !isDeleted && !isDeactivated;
      });
      if (activeSuppliers.length !== selectedComplianceSupplier.length) {
        setSelectedComplianceSupplier(activeSuppliers);
      }
    }
  }, [users, selectedComplianceSupplier, setSelectedComplianceSupplier]);

  const isVariantAvailable = true;

  return (
    <Grid container spacing={2}>
      <Grid item sm={12}>
        <CustomFormTextFieldV2
          label={"Description"}
          required={true}
          placeholder="Description"
          retuired={true}
          value={description}
          setValue={setDescription}
        />
      </Grid>
      <Grid item sm={12}>
        <Grid item sm={12}>
          <Box sx={{ borderBottom: "1px solid black" }}>
            <Typography
              sx={{ ...componentStyle.font.cardHeadder, textAlign: "left" }}
            >
              Selected Assignees
            </Typography>
          </Box>
        </Grid>

        <Grid item sm={12}>
          <CustomMultiSelectV2
            label={"Search/Select Compliance Requestors "}
            selectedValues={selectedComplianceRequestors}
            setSelectedValues={setSelectedComplianceRequestor}
            optionsList={
              Array.isArray(selectedComponentRequestor)
                ? selectedComponentRequestor.map((el) => {
                    const userObj = users.find(
                      (u) => String(u.user_id) === String(el.user_id),
                    );
                    const isDeleted = Boolean(
                      (users.length > 0 && !userObj) ||
                        userObj?.is_deleted === true ||
                        userObj?.deleted === true ||
                        el?.is_deleted === true ||
                        el?.deleted === true,
                    );
                    const isDeactivated = Boolean(
                      userObj
                        ? userObj.is_active === false ||
                            userObj.is_active === 0 ||
                            userObj.status === "inactive"
                        : el.is_active === false ||
                            el.is_active === 0 ||
                            el.status === "inactive",
                    );
                    const compName = getComponentNameForUser(
                      el,
                      formType,
                      selectedComponent,
                      userObj || el,
                    );
                    const displayName =
                      el.name ||
                      el.fullname ||
                      userObj?.fullname ||
                      userObj?.name ||
                      el.email ||
                      userObj?.email ||
                      "";
                    return {
                      value: el.user_id,
                      label: displayName,
                      is_active: !isDeactivated && !isDeleted,
                      is_deleted: isDeleted,
                      disabled: isDeactivated || isDeleted,
                      role_name:
                        userObj?.role_details?.role_name ||
                        userObj?.role_name ||
                        el?.role_details?.role_name ||
                        el?.role_name ||
                        "OEM",
                      component_name: compName || "Description",
                    };
                  })
                : []
            }
            getOptionDisabled={(option) =>
              Boolean(
                option.disabled ||
                  option.is_active === false ||
                  option.is_deleted === true,
              )
            }
            renderOption={(optionProps, option) => {
              const isDisabled = Boolean(
                option.disabled ||
                  option.is_active === false ||
                  option.is_deleted === true,
              );
              return (
                <li
                  {...optionProps}
                  style={{
                    ...optionProps.style,
                    padding: "8px 16px",
                    ...(isDisabled && {
                      opacity: 0.6,
                      cursor: "not-allowed",
                      backgroundColor: "rgba(0, 0, 0, 0.04)",
                    }),
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      width: "100%",
                    }}
                  >
                    <Box>
                      {option.label}
                      {renderUserStatusTag(option)}
                    </Box>
                    <Tooltip
                      title={
                        <Box>
                          <Typography variant="body2">
                            Role: {option.role_name}
                          </Typography>
                          <Typography variant="body2">
                            Component: {option.component_name}
                          </Typography>
                        </Box>
                      }
                      placement="right"
                    >
                      <InfoOutlinedIcon
                        sx={{
                          fontSize: "16px",
                          color: "#000000",
                          ml: 1,
                          pointerEvents: "auto",
                        }}
                      />
                    </Tooltip>
                  </Box>
                </li>
              );
            }}
            required={true}
          />
        </Grid>

        <Grid item sm={12}>
          <CustomMultiSelectV2
            label={"Search/Select Compliance Reviewers "}
            selectedValues={selectedComplianceReviewers}
            setSelectedValues={setSelectedComplianceReviewers}
            optionsList={
              Array.isArray(selectedLegalTechUserDetails)
                ? selectedLegalTechUserDetails.map((el) => {
                    const userObj = users.find(
                      (u) => String(u.user_id) === String(el.user_id),
                    );
                    const isDeleted = Boolean(
                      (users.length > 0 && !userObj) ||
                        userObj?.is_deleted === true ||
                        userObj?.deleted === true ||
                        el?.is_deleted === true ||
                        el?.deleted === true,
                    );
                    const isDeactivated = Boolean(
                      userObj
                        ? userObj.is_active === false ||
                            userObj.is_active === 0 ||
                            userObj.status === "inactive"
                        : el.is_active === false ||
                            el.is_active === 0 ||
                            el.status === "inactive",
                    );
                    const displayName =
                      el.name ||
                      el.fullname ||
                      userObj?.fullname ||
                      userObj?.name ||
                      el.email ||
                      userObj?.email ||
                      "";
                    return {
                      value: el.user_id,
                      label: displayName,
                      is_active: !isDeactivated && !isDeleted,
                      is_deleted: isDeleted,
                      disabled: isDeactivated || isDeleted,
                      role_name:
                        userObj?.role_details?.role_name ||
                        userObj?.role_name ||
                        el?.role_details?.role_name ||
                        el?.role_name ||
                        el.name ||
                        el.fullname ||
                        "Reviewer",
                      chapter: el.chapter,
                    };
                  })
                : []
            }
            getOptionDisabled={(option) =>
              Boolean(
                option.disabled ||
                  option.is_active === false ||
                  option.is_deleted === true,
              )
            }
            renderOption={(optionProps, option) => {
              const isDisabled = Boolean(
                option.disabled ||
                  option.is_active === false ||
                  option.is_deleted === true,
              );
              return (
                <li
                  {...optionProps}
                  style={{
                    ...optionProps.style,
                    padding: "8px 16px",
                    ...(isDisabled && {
                      opacity: 0.6,
                      cursor: "not-allowed",
                      backgroundColor: "rgba(0, 0, 0, 0.04)",
                    }),
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      width: "100%",
                    }}
                  >
                    <Box>
                      {option.label}
                      {renderUserStatusTag(option)}
                    </Box>
                    <Tooltip
                      title={
                        <Box>
                          <Typography variant="body2">
                            Role: {option.role_name}
                          </Typography>
                          <Typography variant="body2">
                            Chapter: {option.chapter}
                          </Typography>
                        </Box>
                      }
                      placement="right"
                    >
                      <InfoOutlinedIcon
                        sx={{
                          fontSize: "16px",
                          color: "#000000",
                          ml: 1,
                          pointerEvents: "auto",
                        }}
                      />
                    </Tooltip>
                  </Box>
                </li>
              );
            }}
            required={true}
          />
        </Grid>

        <Grid item sm={12}>
          <CustomMultiSelectV2
            label={"Search/Select Suppliers"}
            selectedValues={selectedComplianceSupplier}
            setSelectedValues={setSelectedComplianceSupplier}
            optionsList={
              Array.isArray(selectedComponentSupplierDetails)
                ? selectedComponentSupplierDetails.map((el) => {
                    const userObj = users.find(
                      (u) => String(u.user_id) === String(el.user_id),
                    );
                    const isDeleted = Boolean(
                      (users.length > 0 && !userObj) ||
                        userObj?.is_deleted === true ||
                        userObj?.deleted === true ||
                        el?.is_deleted === true ||
                        el?.deleted === true,
                    );
                    const isDeactivated = Boolean(
                      userObj
                        ? userObj.is_active === false ||
                            userObj.is_active === 0 ||
                            userObj.status === "inactive"
                        : el.is_active === false ||
                            el.is_active === 0 ||
                            el.status === "inactive",
                    );
                    const compName = getComponentNameForUser(
                      el,
                      formType,
                      selectedComponent,
                      userObj || el,
                    );
                    const displayName =
                      el.name ||
                      el.fullname ||
                      userObj?.fullname ||
                      userObj?.name ||
                      el.email ||
                      userObj?.email ||
                      "";
                    return {
                      value: el.user_id,
                      label: displayName,
                      is_active: !isDeactivated && !isDeleted,
                      is_deleted: isDeleted,
                      disabled: isDeactivated || isDeleted,
                      role_name:
                        userObj?.role_details?.role_name ||
                        userObj?.role_name ||
                        el?.role_details?.role_name ||
                        el?.role_name ||
                        "Supplier",
                      component_name: compName || "Description",
                    };
                  })
                : []
            }
            getOptionDisabled={(option) =>
              Boolean(
                option.disabled ||
                  option.is_active === false ||
                  option.is_deleted === true,
              )
            }
            renderOption={(optionProps, option) => {
              const isDisabled = Boolean(
                option.disabled ||
                  option.is_active === false ||
                  option.is_deleted === true,
              );
              return (
                <li
                  {...optionProps}
                  style={{
                    ...optionProps.style,
                    padding: "8px 16px",
                    ...(isDisabled && {
                      opacity: 0.6,
                      cursor: "not-allowed",
                      backgroundColor: "rgba(0, 0, 0, 0.04)",
                    }),
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      width: "100%",
                    }}
                  >
                    <Box>
                      {option.label}
                      {renderUserStatusTag(option)}
                    </Box>
                    <Tooltip
                      title={
                        <Box>
                          <Typography variant="body2">
                            Role: {option.role_name}
                          </Typography>
                          <Typography variant="body2">
                            Component: {option.component_name}
                          </Typography>
                        </Box>
                      }
                      placement="right"
                    >
                      <InfoOutlinedIcon
                        sx={{
                          fontSize: "16px",
                          color: "#000000",
                          ml: 1,
                          pointerEvents: "auto",
                        }}
                      />
                    </Tooltip>
                  </Box>
                </li>
              );
            }}
          />
        </Grid>
      </Grid>
      <Grid item sm={12}>
        <Grid item sm={12}>
          <Box
            sx={{
              borderBottom: "1px solid black",
              width: "100%",
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <Typography
              sx={{ ...componentStyle.font.cardHeadder, textAlign: "left" }}
            >
              Regulatory Templates
            </Typography>
            <Box sx={{ pr: 1, pb: 0.5 }}>
              <AddButtonV2
                handleOnClick={() => {
                  setShowDynamicSelect((prev) => {
                    setSelectedDynamicForm(null);
                    return !prev;
                  });
                }}
              />
            </Box>
          </Box>
        </Grid>

        {showDynamicSelect ? (
          <Grid container>
            <Grid item xs={12}>
              <CustomTabsV2
                tabs={[
                  {
                    name: "Default Templates",
                    count: "",
                    content: (
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "space-between",
                          gap: 1,
                        }}
                      >
                        {isVariantAvailable && formTypeId && (
                          <CustomAutoCompleteWithAPIV2
                            key={formTypeId}
                            apiAction={typeApprovalAPI.get_default_templates}
                            searchKey={"name"}
                            placeholder={"Search Template"}
                            skipInitialSearch={false}
                            firstItemDefaultSelect={true}
                            getSelectedItem={(selectedTemplate) => {
                              setSelectedDynamicForm(selectedTemplate);
                              if (
                                selectedTemplate?.template_tag === "OLD" ||
                                selectedTemplate?.template_tag === "NEW"
                              ) {
                                setDynamicFormFilteredData({
                                  master_template_id: Number(
                                    selectedTemplate?.id,
                                  ),
                                  name: selectedTemplate?.name,
                                  type: selectedTemplate?.type,
                                  template_tag: selectedTemplate?.template_tag,
                                  fields: selectedTemplate?.fields,
                                  headers: selectedTemplate?.headers,
                                  footers: selectedTemplate?.footers,
                                  signatures: selectedTemplate?.signatures,
                                  status: selectedTemplate?.status,
                                });
                              }
                            }}
                            displayKey={"name"}
                            valueObj={selectedDynamicForm}
                            params={{
                              type: formTypeId,
                              page_number: 1,
                              page_size: 100,
                            }}
                          />
                        )}
                        <CustomButtonV2
                          size="small"
                          disabled={!selectedDynamicForm}
                          type="submit"
                          btnName="Edit"
                          handleOnClick={handelEditDynamicForm}
                        />
                      </Box>
                    ),
                  },
                  {
                    name: "My Templates",
                    count: "",
                    content: (
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "space-between",
                          gap: 1,
                        }}
                      >
                        {isVariantAvailable && formTypeId && (
                          <CustomAutoCompleteWithAPIV2
                            key={formTypeId}
                            apiAction={typeApprovalAPI.get_user_dynamic_forms}
                            searchKey={"name"}
                            placeholder={"Search Template"}
                            skipInitialSearch={false}
                            firstItemDefaultSelect={true}
                            getSelectedItem={(selectedTemplate) => {
                              setSelectedDynamicForm(selectedTemplate);
                            }}
                            displayKey={"name"}
                            valueObj={selectedDynamicForm}
                            params={{
                              type: formTypeId,
                              project_id: Number(
                                selectedProjectDetails?.project_id,
                              ),
                              variant_id: Number(
                                selectedVariantDetails?.variant_id,
                              ),
                              page_number: 1,
                              page_size: 100,
                            }}
                          />
                        )}
                        <CustomButtonV2
                          size="small"
                          disabled={!selectedDynamicForm}
                          type="submit"
                          btnName="Edit"
                          handleOnClick={handelEditDynamicForm}
                        />
                      </Box>
                    ),
                  },
                ]}
              />
            </Grid>
          </Grid>
        ) : null}
      </Grid>
      <Grid item sm={12}>
        <Box sx={{ display: "flex", justifyContent: "flex-start" }}>
          <CustomButtonV2
            btnName={"Submit"}
            disabled={localSubmitDisabled}
            handleOnClick={handleLocalSaveForm}
          />
        </Box>
      </Grid>
      <Grid item sm={12}>
        <Grid item sm={12}>
          <Box sx={{ borderBottom: "1px solid black" }}>
            <Typography
              sx={{ ...componentStyle.font.cardHeadder, textAlign: "left" }}
            >
              Saved Linkages
            </Typography>
          </Box>
        </Grid>
        <Grid item sm={12}>
          {savedLinkageCard}
        </Grid>
      </Grid>
      {openEditDynamicForm && (
        <DynamicFormRenderer
          isOpen={openEditDynamicForm}
          onClose={handelEditDynamicForm}
          data={selectedDynamicForm}
          setDynamicFormData={setSelectedDynamicForm}
          dynamicFormFilteredData={dynamicFormFilteredData}
          setDynamicFormFilteredData={setDynamicFormFilteredData}
          selectedComponent={selectedComponent}
        />
      )}
      {isDynamicFormDialogOpen && (
        <DynamicFormDialog
          open={isDynamicFormDialogOpen}
          onClose={handleDynamicFormDialog}
          dynamicFormData={selectedDynamicForm}
          setDynamicFormData={setSelectedDynamicForm}
          dynamicFormFilteredData={dynamicFormFilteredData}
          setDynamicFormFilteredData={setDynamicFormFilteredData}
          dialogTitle={"Dynamic Form/Template"}
          dialogContent={null}
          onConfirm={handleAddDescDialogClose}
          onDisabled={() => {}}
          selectedComponent={selectedComponent}
        />
      )}
    </Grid>
  );
}

RaiseTicketRightTemplate.propTypes = {
  selectedEEMain: PropTypes.any,
  selectedComponentRequestor: PropTypes.array,
  selectedLegalTechUserDetails: PropTypes.array,
  selectedComponentSupplierDetails: PropTypes.array,
  setSelectedComponentSupplierDetails: PropTypes.func,
  selectedComplianceRequestors: PropTypes.array,
  selectedComplianceReviewers: PropTypes.array,
  setSelectedComplianceRequestor: PropTypes.func,
  setSelectedComplianceReviewers: PropTypes.func,
  selectedComplianceSupplier: PropTypes.array,
  setSelectedComplianceSupplier: PropTypes.func,
  formType: PropTypes.string,
  selectedDynamicForm: PropTypes.object,
  setSelectedDynamicForm: PropTypes.func,
  handleLocalSaveForm: PropTypes.func,
  localSubmitDisabled: PropTypes.bool,
  description: PropTypes.string,
  setDescription: PropTypes.func,
  savedLinkageCard: PropTypes.node,
  dynamicFormFilteredData: PropTypes.object,
  setDynamicFormFilteredData: PropTypes.func,
  showDynamicSelect: PropTypes.bool,
  setShowDynamicSelect: PropTypes.func,
  selectedComponent: PropTypes.any,
};

export default RaiseTicketRightTemplate;
